package com.schoolconnect.app.feature.notices

import com.schoolconnect.app.feature.common.PaginationPolicy

data class NoticeFilters(
    val audience: String = "",
    val status: String = "",
    val publishedDate: String = "All",
    val publishedDateFrom: String = "",
    val publishedDateTo: String = ""
)

data class NoticeRowUi(
    val id: String,
    val backendId: String = id,
    val title: String,
    val audience: String,
    val publishedOn: String,
    val expiryDate: String,
    val status: String,
    val hasAttachments: Boolean = false,
    val attachmentFileIds: List<String> = emptyList(),
    val attachmentFileUrls: List<String> = emptyList()
)

data class NoticeDetailUi(
    val id: String,
    val title: String,
    val content: String,
    val audience: String,
    val expiryDate: String,
    val status: String,
    val createdAt: String,
    val updatedAt: String,
    val attachmentFileIds: List<String> = emptyList(),
    val attachmentFileUrls: List<String> = emptyList(),
    val attachments: List<NoticeAttachmentDetailUi> = emptyList(),
    val createdBy: String = "Not available",
    val updatedBy: String = "Not available",
    val classTargetIds: List<String> = emptyList()
)

data class NoticeAttachmentDetailUi(
    val fileId: String,
    val url: String,
    val name: String,
    val mimeType: String,
    val sizeLabel: String,
    val previewBytes: ByteArray? = null
)

data class NoticeDraft(
    val title: String,
    val content: String,
    val audience: String,
    val expiresAt: String,
    val isActive: Boolean,
    val attachmentFileIds: List<String>,
    val classTargetIds: List<String> = emptyList(),
    /** Original lifecycle state when editing; null for a newly created notice. */
    val originalStatus: String? = null
)

data class NoticeAudienceOption(
    val code: String,
    val label: String
)

data class NoticeClassTargetOption(
    val id: String,
    val label: String
)

data class NoticesUiState(
    val isLoading: Boolean = true,
    val isStatisticsLoading: Boolean = true,
    val errorMessage: String = "",
    val saveErrorMessage: String = "",
    val searchQuery: String = "",
    val filters: NoticeFilters = NoticeFilters(),
    val rows: List<NoticeRowUi> = emptyList(),
    val pageIndex: Int = 0,
    val pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
    val totalCount: Int = 0,
    val totalNotices: Int = 0,
    val draftNotices: Int = 0,
    val activeNotices: Int = 0,
    val expiredNotices: Int = 0
    ,val sortField: NoticesSortField = NoticesSortField.PUBLISHED_DATE
    ,val sortAscending: Boolean = false,
    val isSaving: Boolean = false,
    val saveGeneration: Int = 0,
    val viewingNotice: NoticeDetailUi? = null,
    val isViewingNotice: Boolean = false,
    val viewNoticeError: String = ""
    ,val audienceOptions: List<NoticeAudienceOption> = emptyList(),
    val statusOptions: List<NoticeAudienceOption> = emptyList(),
    val classTargetOptions: List<NoticeClassTargetOption> = emptyList()
) {
}

sealed interface NoticesUiEvent {
    data object BackClicked : NoticesUiEvent
    data object RefreshClicked : NoticesUiEvent
    data class SearchChanged(val query: String) : NoticesUiEvent
    data class FiltersChanged(val filters: NoticeFilters) : NoticesUiEvent
    data object FiltersReset : NoticesUiEvent
    data class PageChanged(val pageIndex: Int) : NoticesUiEvent
    data class PageSizeChanged(val pageSize: Int) : NoticesUiEvent
    data class SortChanged(val field: NoticesSortField) : NoticesUiEvent
    data class CreateNotice(val draft: NoticeDraft) : NoticesUiEvent
    data class UpdateNotice(val noticeId: String, val draft: NoticeDraft) : NoticesUiEvent
    data class ViewNotice(val noticeId: String) : NoticesUiEvent
    data class DeleteNotice(val noticeId: String) : NoticesUiEvent
}

enum class NoticesSortField { ID, TITLE, AUDIENCE, PUBLISHED_DATE, EXPIRY_DATE, STATUS }

internal fun noticesComparator(field: NoticesSortField): Comparator<NoticeRowUi> = when (field) {
    NoticesSortField.ID -> compareBy { it.id }
    NoticesSortField.TITLE -> compareBy { it.title }
    NoticesSortField.AUDIENCE -> compareBy { it.audience }
    NoticesSortField.PUBLISHED_DATE -> compareBy { it.publishedOn }
    NoticesSortField.EXPIRY_DATE -> compareBy { it.expiryDate }
    NoticesSortField.STATUS -> compareBy { it.status }
}
